const BASE = import.meta.env.VITE_API_BASE || '';

const ACCESS_KEY = 'vpnsaler_access';

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  set(access: string) {
    localStorage.setItem(ACCESS_KEY, access);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
  },
};

async function request<T>(path: string, init: RequestInit = {}, opts: { auth?: boolean } = { auth: true }): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };
  if (opts.auth && tokenStore.access) headers['Authorization'] = `Bearer ${tokenStore.access}`;

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface Plan {
  code: string;
  days: number;
  price: number;
  per_day: string;
  label: string;
  popular: boolean;
}
export type Protocol = 'wireguard' | 'vless';
export type SubStatus = 'pending' | 'active' | 'expired' | 'disabled';

export interface Subscription {
  id: number;
  protocol: Protocol;
  status: SubStatus;
  device_label: string | null;
  plan_code: string | null;
  plan_label: string | null;
  total_days: number | null;
  days_left: number;
  active_since: string | null;
  expires_at: string | null;
}

export interface User {
  id: number;
  tg_id: number | null;
  username: string | null;
  email: string | null;
  name: string | null;
  email_verified: boolean;
  created_at: string;
}

export interface AuthOut {
  access: string;
  refresh?: string;
  user: User;
}

export type PayStatus = 'pending' | 'paid' | 'failed' | 'trial';

export interface CreateOrderIn {
  plan_code: string;
  protocol: Protocol;
  device_label?: string;
  target_subscription_id?: number;
}

export interface CreateOrderOut {
  order_id: string;
  amount: number;
  currency?: string;
  status: PayStatus;
  confirmation_url: string | null;
  auto_confirmed: boolean;
}

export interface CreateTmaInvoiceOut {
  order_id: string;
  amount: number;
  currency: string;
  invoice_link: string;
  plan_label: string;
}

export interface OrderStatus {
  order_id: string;
  status: PayStatus;
  amount: number;
  plan_label: string;
  protocol: Protocol | null;
  created_at: string;
  subscription_id: number | null;
}

export interface SubscriptionConfig {
  subscription_id: number;
  protocol: Protocol;
  filename: string;
  content: string;
  link: string | null;
  qr_png_base64: string;
  note?: string | null;
}

export interface Payment {
  id: number;
  order_id: string;
  plan_label: string;
  protocol: Protocol | null;
  amount: number;
  status: PayStatus;
  created_at: string;
}

export interface ReferralStats {
  code: string;
  link: string;
  invited_count: number;
  paid_count: number;
  bonus_days_received: number;
}

export const api = {
  auth: {
    tma: (init_data: string) =>
      request<AuthOut>('/api/auth/tma', { method: 'POST', body: JSON.stringify({ init_data }) }, { auth: false }),
  },
  plans: { list: () => request<Plan[]>('/api/plans', {}, { auth: false }) },
  me: { get: () => request<User>('/api/me') },
  subs: {
    list: () => request<Subscription[]>('/api/subscriptions'),
    getConfig: (id: number) => request<SubscriptionConfig>(`/api/subscriptions/${id}/config`),
    reissue: (id: number) =>
      request<SubscriptionConfig>(`/api/subscriptions/${id}/reissue`, { method: 'POST' }),
    downloadTicket: (id: number) =>
      request<{ url: string; file_name: string; expires_at: string }>(
        `/api/subscriptions/${id}/download-ticket`,
        { method: 'POST' },
      ),
  },
  payments: {
    list: () => request<Payment[]>('/api/payments'),
    create: (body: CreateOrderIn) =>
      request<CreateOrderOut>('/api/payments/create', { method: 'POST', body: JSON.stringify(body) }),
    createTmaInvoice: (body: CreateOrderIn) =>
      request<CreateTmaInvoiceOut>('/api/payments/tma-invoice', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    get: (orderId: string) => request<OrderStatus>(`/api/payments/${encodeURIComponent(orderId)}`),
  },
  referral: { get: () => request<ReferralStats>('/api/referral') },
};
